/**
 * MODEL RESOLUTION (docs/MODELER.md "MODEL RESOLUTION round", Matt 2026-08-17).
 *
 * The model's finest meaningful coordinate step: 1/64 inch (MODEL PRECISION round,
 * Matt 2026-10-08 — was 1/256" from 2026-08-17; 1/64" matches the body kernel's weld
 * tolerance and the 1/64" that every dimension now displays, so a clicked/dragged
 * point and a dimension's text can never disagree). Every imperial dimension the team
 * types (halves down to 64ths) is an EXACT multiple, so the grid only ever cleans up
 * raw cursor input, never a typed dimension. It is
 * comfortably coarser than clipper.ts's SCALE=1e4 (0.0001 ft), so the geometry
 * engine fully distinguishes grid points, and far below anything visible,
 * dimensionable, or buildable.
 *
 * WHERE IT APPLIES — free degrees of freedom ONLY (the SketchUp length-snapping
 * cautionary tale, and the Smith-kitchen lesson that rounding at the wrong layer
 * MANUFACTURES misalignment):
 *  - a raw click (`resolveSnap` kind 'none'): both coordinates quantize;
 *  - an ortho-constrained run (kind 'ortho'): only the FREE coordinate/length
 *    quantizes — the anchor-derived coordinate stays bit-exact;
 *  - snapped landings (node/midpoint/edge/axis) are NEVER quantized: pulling a
 *    point off the edge it snapped to would recreate the exact bug class this
 *    round exists to kill. Exact coincidence with real geometry always outranks
 *    grid purity.
 * DERIVED geometry (face offsets, plane solves, miters) is never quantized —
 * full precision + epsilon comparisons, per the linework-precision round.
 *
 * It is FIXED and invisible — not a setting (no way to configure it wrong).
 */

/** 1/64 inch in decimal feet (1/768 ft ≈ 0.0013 ft ≈ 0.0156"). */
export const MODEL_RESOLUTION_FT = 1 / 768

/** Round a feet scalar to the model resolution grid. */
export function quantFt(v: number): number {
  const r = Math.round(v * 768) / 768
  return r === 0 ? 0 : r
}

/** Round both components of a 2D point (any coordinate frame) to the grid. */
export function quantPt(pt: readonly [number, number]): [number, number] {
  return [quantFt(pt[0]), quantFt(pt[1])]
}
